import { apiStatus, TEST_IDS } from '@va/constants';
import { startUpgradeProcess } from '@va/dashboard/actions/app';
import {
  getCurrentPlanName,
  getFeaturePermissions,
  getFeaturePermissionsStatus,
  getUnlimitedVoucher,
} from '@va/dashboard/selectors/app';
import { getWebsiteTimezone } from '@va/dashboard/selectors/core';
import { useLocalizationContext } from '@va/localization';
import { subscriptionStatus } from '@va/standalone/shared/constants';
import { getNoOfDaysUntil, isAccountSettingsPageSelected } from '@va/standalone/shared/helpers';
import {
  getLastCreatedSubscription,
  getLastSubscriptionExpirationDate,
  hasAppliedLifeTimeDeal as hasAppliedLifeTimeDealSelector,
  hasCanceledSubscription as hasCanceledSubscriptionSelector,
  hasFreePlan as hasFreePlanSelector,
  hasPermission,
  isSettingActiveWebsiteInProgress as isSettingActiveWebsiteInProgressSelector,
} from '@va/standalone/shared/selectors';
import { PreHeaderBadge } from '@va/ui/design-system';
import { addNumberSeparator, isStandaloneApp } from '@va/util/helpers';
import moment from 'moment';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

export function UpgradeBadge() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { translate, locale } = useLocalizationContext();
  const nowUnixTs = moment().unix();
  const { pathname } = useLocation();
  const timezone = useSelector(getWebsiteTimezone);
  const hasFreePlan = useSelector(hasFreePlanSelector);
  const lastSubscription = useSelector(getLastCreatedSubscription);
  const hasChangeSubscriptionPermission = useSelector(hasPermission);
  const isActiveUntil = useSelector(getLastSubscriptionExpirationDate);
  const hasAppliedLifeTimeDeal = useSelector(hasAppliedLifeTimeDealSelector);
  const subscriptionStatusValue = lastSubscription && lastSubscription.status;
  const hasCanceledSubscription = useSelector(hasCanceledSubscriptionSelector);
  const noOfActiveSubscriptionDaysLeft = getNoOfDaysUntil(isActiveUntil, timezone);
  const isSettingActiveWebsiteInProgress = useSelector(isSettingActiveWebsiteInProgressSelector);
  const unlimitedVoucher = useSelector(getUnlimitedVoucher);
  const featurePermissionsStatus = useSelector(getFeaturePermissionsStatus);
  const canceledState =
    hasCanceledSubscription &&
    (lastSubscription.status === subscriptionStatus.ACTIVE || lastSubscription.status === subscriptionStatus.PAUSED);

  const currentPlanName = useSelector((state) => getCurrentPlanName(state, subscriptionStatus.ACTIVE));
  const permissions = useSelector(getFeaturePermissions);

  const messageType = noOfActiveSubscriptionDaysLeft === 0 ? 'expired' : 'counting';

  const text = useMemo(() => {
    if (!canceledState)
      return {
        message: translate('freePlan.upgradeBadgeV2.40daysLimit', {
          planName: currentPlanName,
          recordingsLimit: addNumberSeparator(permissions?.recordings?.limit ?? 0, locale),
          heatmapsLimit: addNumberSeparator(permissions?.heatmaps?.limit ?? 0, locale),
          funnelsLimit: addNumberSeparator(permissions?.funnels?.limit ?? 0, locale),
          pollsLimit: addNumberSeparator(permissions?.polls?.limit ?? 0, locale),
          surveysLimit: addNumberSeparator(permissions?.surveys?.limit ?? 0, locale),
        }),
        actionText: translate('freePlan.upgradeBadgeV2.cta'),
      };

    if (subscriptionStatusValue === subscriptionStatus.PAUSED) {
      return {
        message: translate('upgradeBadge.subscriptionPaused'),
        actionText: translate('button.renewNow'),
      };
    }
    return {
      message: translate(`upgradeBadge.cancelRequested.${messageType}`, {
        count: noOfActiveSubscriptionDaysLeft,
      }),
      actionText: translate('button.turnOnAutoRenewal'),
    };
  }, [
    canceledState,
    currentPlanName,
    locale,
    messageType,
    noOfActiveSubscriptionDaysLeft,
    permissions?.funnels?.limit,
    permissions?.heatmaps?.limit,
    permissions?.polls?.limit,
    permissions?.recordings?.limit,
    permissions?.surveys?.limit,
    subscriptionStatusValue,
    translate,
  ]);

  if (
    isAccountSettingsPageSelected(pathname) ||
    pathname.includes('upgrade') ||
    hasAppliedLifeTimeDeal ||
    (!hasFreePlan && (!hasCanceledSubscription || nowUnixTs > isActiveUntil)) ||
    isSettingActiveWebsiteInProgress ||
    unlimitedVoucher ||
    featurePermissionsStatus === apiStatus.IN_PROGRESS
  )
    return null;

  return (
    <PreHeaderBadge
      color={canceledState ? 'danger' : 'warning'}
      message={text.message}
      actionText={text.actionText}
      onActionClick={() => {
        if (!hasChangeSubscriptionPermission) return;

        if (messageType === 'counting' && canceledState && isStandaloneApp()) {
          history.push('/settings/subscription#payment-method');
          return;
        }

        dispatch(startUpgradeProcess());
      }}
      isActionEnabled={hasChangeSubscriptionPermission}
      actionTooltip={!hasChangeSubscriptionPermission && translate('tooltips.contributorRole')}
      dataTestId={canceledState ? TEST_IDS.generic.banner.danger : TEST_IDS.generic.banner.warning}
    />
  );
}
